import { effect, signal, type WritableSignal } from '@angular/core';

/**
 * 一个**会写回 `localStorage`** 的布尔开关：进来读一次，之后每次改动静默写回。
 *
 * <p>用途是「记住界面上那几个开关」—— 侧栏收没收、发送区展没展开。它们**不是应用状态**，
 * 丢了最多是不顺手，所以读不出、写不进（隐私模式、站点数据被拦）都静默回落，绝不往外抛：
 * 这类偏好不该有能力让页面起不来。落库的写法与 `uart.service.ts` 的 `saveOptions` 同一口径。</p>
 *
 * <p>认的口径只有 `'true'` / `'false'` 两个字面量（同 `theme.service.ts` 的 `dark_mode`），
 * 其余一律用 `fallback` —— 存储是一份可能过期、也可能被手改的外部输入，
 * 坏值应当回落而不是当成 `true` 或 `false` 用。</p>
 *
 * <p>**必须在注入上下文里调用**（组件的字段初始化器或构造函数）：落库那条走的是 `effect`。</p>
 */
export function persistedBool(key: string, fallback: boolean): WritableSignal<boolean> {
  const state = signal(readBool(key, fallback));
  effect(() => writeBool(key, state()));
  return state;
}

/**
 * 读。没存过、存的是别的字面量、存储不可用 —— 三种情况都给 `fallback`。
 *
 * 这里 `JSON.parse` 用不上，所以也不必像 `UartOptionsCodec` 那样逐字段校验：
 * 布尔只有两个字面量，认不出就是认不出。
 */
function readBool(key: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return fallback;
  } catch {
    // 隐私模式下 localStorage 会抛。偏好读不出来用默认值就是了，别把整个组件带崩。
    return fallback;
  }
}

function writeBool(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* 写不进去就算了：只是下次进来不记得，不影响本次会话 */
  }
}
